import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export type UserRole = 'admin' | 'executivo' | 'ar' | 'producao' | 'financeiro' | 'viewer' | 'operador';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  status?: 'pending' | 'approved' | 'rejected';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasPermission: (requiredRoles: UserRole[]) => boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar sessão existente
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadUserProfile(session.user.id, session.user.email || '');
      } else {
        setLoading(false);
      }
    });

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadUserProfile(session.user.id, session.user.email || '');
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (userId: string, email: string) => {
    try {
      // Tentar buscar o perfil existente
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Se o perfil não existe, criar como pendente (exige aprovação de um admin)
      // Nunca auto-aprovar ou dar role admin — evita que convidados virem admin ao logar
      if (!data) {
        const newUser = {
          id: userId,
          name: email.split('@')[0],
          email: email,
          role: 'operador' as UserRole,
          status: 'pending' as const,
          avatar: null,
        };

        const { data: createdUser, error: createError } = await supabase
          .from('users')
          .upsert([newUser], { onConflict: 'id' })
          .select()
          .single();

        if (createError) {
          console.error('Erro ao criar perfil pendente:', createError);
          // Se não conseguir criar perfil (ex.: RLS), tratar como pendente e redirecionar
          await supabase.auth.signOut();
          setUser(null);
          window.location.href = '/login?status=pending';
          return;
        }

        // Perfil criado como pendente: não permitir acesso até aprovação
        if (createdUser.status === 'pending' || createdUser.status === 'rejected') {
          await supabase.auth.signOut();
          setUser(null);
          window.location.href = '/login?status=pending';
          return;
        }

        setUser({
          id: createdUser.id,
          name: createdUser.name,
          email: createdUser.email,
          role: createdUser.role,
          avatar: createdUser.avatar,
          status: createdUser.status,
        });
      } else {
        // Verificar status antes de definir o usuário
        if (data.status === 'pending' || data.status === 'rejected') {
          await supabase.auth.signOut();
          setUser(null);
          window.location.href = '/login?status=pending';
          return;
        }

        setUser({
          id: data.id,
          name: data.name,
          email: data.email,
          role: data.role,
          avatar: data.avatar,
          status: data.status,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      // Não lançar erro, apenas logar para não bloquear o login
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      await loadUserProfile(data.user.id, data.user.email || '');
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    // Redirecionar para login após logout
    window.location.href = '/login';
  };

  const hasPermission = (requiredRoles: UserRole[]) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, hasPermission, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}